import Decimal from 'decimal.js';
import { decimalToCents, centsToDecimal } from '../lib/decimal';
import { isInPriorYear } from '../lib/timezone';
import type {
  CumulativeRoomInput,
  ResidencyYear,
  TFSAState,
  OverContribResult,
  SimulatorResult,
  RunningPenaltyResult
} from './types';
import constants from '../data/cra-2026.json';

const ANNUAL_LIMITS_CENTS = constants.tfsa.annual_limits_cents as Record<string, string>;
const TFSA_START_YEAR = 2009;
const MIN_AGE_YEARS = constants.tfsa.min_age_years;

export function eligibleStartYear(birth_year: number, residency_year: ResidencyYear): number {
  const ageEighteenYear = birth_year + MIN_AGE_YEARS;
  const residencyStart = residency_year === 'before-2009' ? TFSA_START_YEAR : residency_year;
  return Math.max(ageEighteenYear, residencyStart, TFSA_START_YEAR);
}

export function cumulativeRoom(input: CumulativeRoomInput): string {
  // P2-3: Defensive guard. Wizard normally parseInt(...,10)s the
  // birth_year, but a corrupted IDB record or programmatic caller could
  // pass NaN / ±Infinity. Math.max in eligibleStartYear silently
  // produces unsafe loop bounds (-Infinity → full $109K leak). Treat
  // any non-finite birth_year as "no eligibility computable".
  if (!Number.isFinite(input.birth_year)) return '0';

  const start = eligibleStartYear(input.birth_year, input.residency_year);
  if (start > input.current_year) return '0';

  let sum = new Decimal(0);
  for (let year = start; year <= input.current_year; year++) {
    const limitStr = ANNUAL_LIMITS_CENTS[String(year)];
    if (!limitStr) continue;
    sum = sum.plus(centsToDecimal(limitStr));
  }
  return decimalToCents(sum);
}

// v1.0.4 P0 INVARIANT (defect C, layer 1 — engine floor):
// A stored contribution with amount_cents < 0 (e.g. a legacy IDB record
// from a v1.0.3 user, or a corrupted import) must NOT be summed
// algebraically into the contribution total — that would let a negative
// contribution INFLATE available room past the cumulative cap and lead a
// Canadian user to over-contribute and incur a 1%/month CRA penalty.
// Each contribution is floored at 0 here. Withdrawals are similarly
// floored for symmetry and defense-in-depth.
const ZERO = new Decimal(0);
function floorAtZero(centsString: string): Decimal {
  return Decimal.max(ZERO, centsToDecimal(centsString));
}

export function availableRoom(state: TFSAState): string {
  const initial = centsToDecimal(state.cra_room_initial_cents);

  const contribTotal = state.contributions.reduce(
    (sum, c) => sum.plus(floorAtZero(c.amount_cents)),
    new Decimal(0)
  );

  const restoredWithdrawals = state.withdrawals
    .filter((w) => isInPriorYear(w.date, state.current_date_iso))
    .reduce((sum, w) => sum.plus(floorAtZero(w.amount_cents)), new Decimal(0));

  return decimalToCents(initial.minus(contribTotal).plus(restoredWithdrawals));
}

export function restoringNextJan(state: TFSAState): string {
  const sum = state.withdrawals
    .filter((w) => !isInPriorYear(w.date, state.current_date_iso))
    .reduce((acc, w) => acc.plus(centsToDecimal(w.amount_cents)), new Decimal(0));
  return decimalToCents(sum);
}

/** $100 — typo-tolerant warning band (under = warning, at-or-over = error). */
const WARNING_BUFFER_CENTS = '10000';

/**
 * Whether the user is logging a brand-new contribution (block + error)
 * or recording one that already happened (allow + warning). Defaults
 * to 'prospective' for backwards compatibility with existing call sites.
 *
 * Added 2026-05-11 to support P0-2 (over-contributed users need to be
 * able to document their existing situation in the PWA).
 */
export type EntryMode = 'prospective' | 'historical';

export function wouldOverContribute(
  state: TFSAState,
  proposedCents: string,
  entry_mode: EntryMode = 'prospective'
): OverContribResult {
  const available = centsToDecimal(availableRoom(state));
  const proposed = centsToDecimal(proposedCents);
  const overBy = proposed.minus(available);

  if (overBy.lessThanOrEqualTo(0)) {
    return {
      over: false,
      by_cents: '0',
      penalty_pct_per_month: '1.0',
      severity: 'ok'
    };
  }

  const inWarningBand = overBy.lessThan(centsToDecimal(WARNING_BUFFER_CENTS));

  // Historical entries are always non-blocking — the user is documenting
  // a contribution they already made, so we downgrade 'error' to
  // 'warning' while still reporting the excess accurately for penalty
  // display.
  const severity: OverContribResult['severity'] =
    inWarningBand || entry_mode === 'historical' ? 'warning' : 'error';

  return {
    over: true,
    by_cents: decimalToCents(overBy),
    penalty_pct_per_month: '1.0',
    severity
  };
}

export function simulator(state: TFSAState, proposedCents: string): SimulatorResult {
  const available = centsToDecimal(availableRoom(state));
  const proposed = centsToDecimal(proposedCents);
  const projected = available.minus(proposed);

  return {
    projected_available_cents: decimalToCents(projected),
    over_contrib: wouldOverContribute(state, proposedCents)
  };
}

/**
 * Whole-calendar-months elapsed between two ISO-8601 date strings
 * (YYYY-MM-DD or longer). Uses local-clock year/month/day components
 * (no UTC offset), consistent with the timezone helpers. Returns 0
 * when end is on/before start.
 */
function monthsBetween(startIso: string, endIso: string): number {
  const s = parseYmd(startIso);
  const e = parseYmd(endIso);
  let months = (e.y - s.y) * 12 + (e.m - s.m);
  if (e.d < s.d) months -= 1;
  return months < 0 ? 0 : months;
}

function parseYmd(iso: string): { y: number; m: number; d: number } {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    throw new Error(`tfsa.parseYmd: invalid ISO string: ${iso}`);
  }
  return {
    y: parseInt(match[1]!, 10),
    m: parseInt(match[2]!, 10),
    d: parseInt(match[3]!, 10)
  };
}

/**
 * Accumulated CRA over-contribution penalty: 1% per month on the prior
 * excess, until the user withdraws. Formula per
 * @reference/tax-data-2026.md §TFSA "Over-contribution penalty":
 *   penalty_cents = excess_cents × 0.01 × months_over
 *
 * Returns zeros when state has no prior_year_excess_started_iso.
 */
export function runningPenalty(
  state: TFSAState,
  current_date_iso: string
): RunningPenaltyResult {
  const startedIso = state.prior_year_excess_started_iso;
  if (!startedIso) {
    return { months_over: 0, penalty_cents: '0' };
  }
  const excess = centsToDecimal(state.prior_year_excess_cents ?? '0');
  const months = monthsBetween(startedIso, current_date_iso);
  const penalty = excess.times(new Decimal('0.01')).times(months);
  return {
    months_over: months,
    penalty_cents: decimalToCents(penalty)
  };
}
